import {
    useInviteMember,
    useOrgAuthStore,
    useOrgMembers
} from "@solomon-apps/shared";
import { ContentBlock } from "@solomon-apps/shared/src/components/console";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import {
    Badge,
    Button,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import { motion, useReducedMotion } from "motion/react";
import React, { useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { DisabledReason } from "@/components/DisabledReason";
import { CLIENT_OAUTH_CONFIG } from "@/constants/oauthConfig";
import { LabeledInput } from "@/pages/shared/LabeledField";

import { RoleChoiceField } from "./Team/components/RoleChoiceField";
import { TeamPageHeader } from "./Team/components/TeamPageHeader";
import {
    type NewInvitationState,
    TEAM_PATH,
    teamMemberPath
} from "./Team/teamData";
import { CREATE_LABELS, useAddMember } from "./Team/useAddMember";

/**
 * Client Console Add Member page (`/team/new`), opened from +Add Member on
 * `/team`. Built like a member's page (`/team/:memberUuid`) so adding someone
 * looks like the page they will have: "Back to Team" above the title, then
 * Profile's 50/50 split with the left column closed by a full-height rule.
 *
 *   - Title: "Add Team Member".
 *   - Left, Account: Name (optional, as invites always were), Email (one
 *     address: the page adds one member) and Role (Member by default), as
 *     Profile's Account fields, then Send Invite beside a same-size Cancel.
 *   - Right: empty. Nothing about a member exists until the invite does.
 *
 * Send Invite carries the outcome (Sending..., Invite Sent, Not Sent, then
 * Retry); why it cannot go yet, or why it failed, is on demand on the
 * button. On success the page moves to the new member's page and hands it
 * the claim link, which that page shows in its right column.
 */
export function TeamAddMemberPage(): React.ReactElement {
    const me = useOrgAuthStore((s) => s.me);
    const navigate = useNavigate();
    const reducedMotion = useReducedMotion() ?? false;

    const { members, refetch } = useOrgMembers(CLIENT_OAUTH_CONFIG);
    const { inviteMemberAsync } = useInviteMember(CLIENT_OAUTH_CONFIG);
    const existingEmails = useMemo(
        () => members.map((m) => m.email),
        [members]
    );

    const form = useAddMember({
        existingEmails,
        invite: async (input) => {
            const response = await inviteMemberAsync(input);
            // The new member's page reads the roster: have them in it before
            // the page moves there, so it never opens on Member Not Found. A
            // failed refresh is not a failed invite.
            try {
                await refetch();
            } catch {
                // The roster refresh is best-effort; the invite stands.
            }
            return response;
        },
        onCreated: (response) => {
            const uuid = response.orgUser?.uuid;
            if (!uuid) {
                navigate(TEAM_PATH);
                return;
            }
            const state: NewInvitationState = {
                invitation: {
                    claimUrl: response.claimUrl,
                    delivery: response.delivery
                }
            };
            navigate(teamMemberPath(uuid), { state });
        }
    });

    // Only Owners invite (the +Add Member button is theirs alone).
    if (me && me.orgRole !== "Owner") {
        return <Navigate to={TEAM_PATH} replace />;
    }

    const busy = form.phase === "creating" || form.phase === "created";
    const emailInvalid = form.emailTouched && form.emailProblem !== null;
    // Before a submit, an incomplete address only disables the button (its
    // reason on demand); a failure keeps the button live for Retry.
    const blockedReason = busy ? null : form.emailProblem;

    const createButton = (
        <Button
            variant="raised"
            interaction="lifted"
            size="md"
            type="submit"
            disabled={busy || blockedReason !== null}
            labels={CREATE_LABELS}
            labelKey={form.phase}
            aria-label={
                form.failure
                    ? `${CREATE_LABELS[form.phase]}: ${form.failure}`
                    : undefined
            }
        />
    );

    return (
        <motion.div
            initial={reducedMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            // Page enter: 400ms, named out-expo (rule: motion/named-easing),
            // the same as /team and a member's page.
            transition={{ duration: 0.4, ease: easings.outExpo }}
            className="flex flex-col pt-3 md:pt-6 w-full min-h-full"
        >
            <form
                noValidate
                onSubmit={(e) => {
                    e.preventDefault();
                    void form.submit();
                }}
                className="flex w-full flex-1 flex-col"
            >
                <TeamPageHeader title="Add Team Member" />

                {/* A member's page split: two equal columns on wide screens,
                    the left closed by a full-height rule; stacked below lg. */}
                <div className="flex w-full flex-1 flex-col lg:flex-row">
                    <div className="flex w-full flex-col py-3 md:py-6 lg:w-1/2 lg:border-r lg:border-line-base">
                        <ContentBlock
                            title="Account"
                            className="px-4 md:px-6 gap-1 md:gap-2"
                        >
                            <div className="flex flex-col gap-3 py-4">
                                <LabeledInput
                                    label="Name"
                                    type="text"
                                    autoComplete="off"
                                    placeholder="Displayed Name"
                                    value={form.fullName}
                                    onChange={(e) =>
                                        form.setFullName(e.target.value)
                                    }
                                    aria-label="Name"
                                    disabled={busy}
                                    autoFocus
                                />
                                <LabeledInput
                                    label="Email"
                                    type="email"
                                    inputMode="email"
                                    autoComplete="off"
                                    placeholder="name@company.com"
                                    value={form.email}
                                    onChange={(e) =>
                                        form.setEmail(e.target.value)
                                    }
                                    onBlur={() => {
                                        if (form.email.trim()) {
                                            form.touchEmail();
                                        }
                                    }}
                                    aria-label="Email"
                                    aria-invalid={emailInvalid || undefined}
                                    aria-description={
                                        emailInvalid
                                            ? (form.emailProblem ?? undefined)
                                            : undefined
                                    }
                                    disabled={busy}
                                    // What to fix, in a word or two, at the
                                    // field's end (never a sentence below it).
                                    actions={
                                        emailInvalid && form.emailProblem ? (
                                            // The error chip without a dot, as
                                            // Already Registered on Add Address.
                                            <Badge variant="fill" color="error">
                                                {form.emailProblem}
                                            </Badge>
                                        ) : undefined
                                    }
                                />
                                <RoleChoiceField
                                    value={form.role}
                                    onChange={form.setRole}
                                    disabled={busy}
                                />
                            </div>

                            <div className="flex flex-wrap items-center gap-4 py-3">
                                {blockedReason !== null ? (
                                    <DisabledReason
                                        reason={blockedReason}
                                        label={CREATE_LABELS[form.phase]}
                                    >
                                        {createButton}
                                    </DisabledReason>
                                ) : (
                                    // One tree for every phase, so the label
                                    // swap cross-fades instead of remounting;
                                    // the tooltip opens only on a failure.
                                    <TooltipProvider>
                                        <Tooltip disabled={!form.failure}>
                                            <TooltipTrigger
                                                render={createButton}
                                            />
                                            <TooltipContent side="top">
                                                {form.failure}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                                {/* Transparent + underline, at Send
                                    Invite's size. */}
                                <Button
                                    variant="transparent"
                                    interaction="underline"
                                    size="md"
                                    render={<Link to={TEAM_PATH} />}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </ContentBlock>
                    </div>
                    {/* The right half stays empty: the frame holds the
                        member page's proportions. */}
                    <div aria-hidden="true" className="w-full lg:w-1/2" />
                </div>
            </form>
        </motion.div>
    );
}
